import React, { useMemo } from 'react';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import css from './CountrySelect.module.css';

const CountrySelect = ({
  value,
  onChange,
  onBlur,
  locale = 'en',
  placeholder,
  id,
  name,
  intl,
  hasError = false,
  pinnedCountryCode = null,
}) => {
  const options = useMemo(() => {
    const countries = countryList().getData();

    const toOption = (country, displayNames) => ({
      value: country.value,
      label: (displayNames && displayNames.of(country.value)) || country.label,
    });

    let displayNames = null;
    if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
      try {
        displayNames = new Intl.DisplayNames([locale], { type: 'region' });
      } catch (e) {
        // ignore and fall back to provided labels
        displayNames = null;
      }
    }

    const mapped = countries.map(country => toOption(country, displayNames));
    const sorted = mapped.sort((a, b) => a.label.localeCompare(b.label, locale));

    const pinned = pinnedCountryCode ? String(pinnedCountryCode).toUpperCase() : null;
    if (pinned) {
      const pinnedIndex = sorted.findIndex(o => String(o.value).toUpperCase() === pinned);
      if (pinnedIndex > 0) {
        const [pinnedOption] = sorted.splice(pinnedIndex, 1);
        sorted.unshift(pinnedOption);
      }
    }

    return sorted;
  }, [locale]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find(option => option.value === value) || null;
  }, [value, options]);

  const handleChange = selectedOption => {
    if (onChange) {
      onChange(selectedOption ? selectedOption.value : '');
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      height: '50px',
      minHeight: '50px',
      backgroundColor: '#fff',
      border: hasError
        ? '1px solid #ff0000'
        : state.isFocused
        ? '1px solid #80bdff'
        : '1px solid #e0e0e0',
      borderRadius: '4px',
      boxShadow: 'none',
      '&:hover': {
        border: hasError ? '1px solid #ff0000' : '1px solid #b8bfd1',
      },
    }),
    menu: provided => ({
      ...provided,
      zIndex: 9999,
      border: '1px solid #e0e0e0',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    }),
    menuList: provided => ({
      ...provided,
      maxHeight: '250px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#ee8b51' : state.isFocused ? '#f0f0f0' : '#fff',
      color: state.isSelected ? '#fff' : '#4a4a4a',
      cursor: 'pointer',
      padding: '10px 12px',
      '&:active': {
        backgroundColor: '#e79565ff',
      },
    }),
    placeholder: provided => ({
      ...provided,
      color: '#4a4a4a',
    }),
    singleValue: provided => ({
      ...provided,
      color: '#4a4a4a',
    }),
    input: provided => ({
      ...provided,
      color: '#4a4a4a',
      margin: 0,
      padding: 0,
    }),
    valueContainer: provided => ({
      ...provided,
      height: '48px',
      padding: '0 12px',
    }),
    dropdownIndicator: provided => ({
      ...provided,
      color: '#4a4a4a',
      '&:hover': {
        color: '#4a4a4a',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
  };

  return (
    <div className={css.countrySelectWrapper}>
      <Select
        id={id}
        name={name}
        value={selectedOption}
        onChange={handleChange}
        onBlur={onBlur}
        options={options}
        placeholder={placeholder}
        styles={customStyles}
        isSearchable
        isClearable
        classNamePrefix="country-select"
        noOptionsMessage={() =>
          intl
            ? intl.formatMessage({
                id: 'CountrySelect.noOptions',
                defaultMessage: 'No countries found',
              })
            : 'No countries found'
        }
      />
    </div>
  );
};

export default CountrySelect;
